import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navigationLinks } from '../../data/mock';
import { cn } from '../../lib/utils';

// Language storage key
const LANG_STORAGE_KEY = 'gdev_language';

// Language toggle only available on homepage
const homepageRoutes = ['/', '/ar', '/ar/'];

// Helper function to determine if path is Arabic
const isArabicPath = (pathname) => {
  return pathname === '/ar' || 
         pathname === '/ar/' || 
         pathname.startsWith('/ar/');
};

// Get language from localStorage with fallback to 'en'
const getStoredLanguage = () => {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY) || 'en';
  } catch {
    return 'en';
  }
};

// Set language in localStorage
const setStoredLanguage = (lang) => {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Silently fail if localStorage is not available
  }
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine if current page is Arabic from URL
  // NOT pages that happen to start with 'ar' like /articles
  const isArabic = isArabicPath(location.pathname);
  
  // Sync language preference to localStorage when navigating
  useEffect(() => {
    const currentLang = isArabic ? 'ar' : 'en';
    setStoredLanguage(currentLang);
  }, [isArabic, location.pathname]);
  
  // Check if language toggle should be shown (only on homepage)
  const showLanguageToggle = homepageRoutes.includes(location.pathname);
  
  // Get the alternate language path (only used on homepage)
  const getAlternateLanguagePath = () => {
    if (isArabic) return '/';
    return '/ar/';
  };

  const alternatePath = getAlternateLanguagePath();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={isArabic ? "/ar/" : "/"} className="flex items-center">
            <span className="text-xl font-semibold tracking-tight text-stone-900">
              G Developments
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {navigationLinks.map((link) => {
              const href = isArabic ? `/ar${link.href === '/' ? '/' : link.href}` : link.href;
              const isActive = location.pathname === href || 
                (link.href === '/' && (location.pathname === '/' || location.pathname === '/ar' || location.pathname === '/ar/'));
              
              return (
                <Link
                  key={link.name}
                  to={href}
                  className={cn(
                    "text-sm font-medium tracking-wide transition-colors duration-300",
                    isActive
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                      : "text-stone-600 hover:text-stone-900"
                  )}
                >
                  {link.name}
                </Link>
              );
            })}
            
            {/* Language Toggle - Only on homepage */}
            {showLanguageToggle && (
              <div className="flex items-center gap-2 text-sm font-medium border-l border-stone-200 pl-6 ml-2">
                <Link
                  to={isArabic ? alternatePath : location.pathname}
                  className={cn(
                    "transition-colors duration-300",
                    !isArabic
                      ? "text-stone-900"
                      : "text-stone-400 hover:text-stone-600"
                  )}
                >
                  EN
                </Link>
                <span className="text-stone-300">|</span>
                <Link
                  to={isArabic ? location.pathname : alternatePath}
                  className={cn(
                    "transition-colors duration-300",
                    isArabic
                      ? "text-stone-900"
                      : "text-stone-400 hover:text-stone-600"
                  )}
                >
                  عربي
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-stone-600 hover:text-stone-900 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          "md:hidden absolute top-full left-0 right-0 bg-white border-b border-stone-100 transition-all duration-300 overflow-hidden",
          mobileMenuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <nav className="px-6 py-4 space-y-4">
          {navigationLinks.map((link) => {
            const href = isArabic ? `/ar${link.href === '/' ? '/' : link.href}` : link.href;
            const isActive = location.pathname === href ||
              (link.href === '/' && (location.pathname === '/' || location.pathname === '/ar' || location.pathname === '/ar/'));
            
            return (
              <Link
                key={link.name}
                to={href}
                className={cn(
                  "block text-sm font-medium tracking-wide transition-colors duration-300",
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                    : "text-stone-600 hover:text-stone-900"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            );
          })}
          
          {/* Mobile Language Toggle - Only on homepage */}
          {showLanguageToggle && (
            <div className="flex items-center gap-3 pt-4 border-t border-stone-100">
              <Link
                to={isArabic ? alternatePath : location.pathname}
                className={cn(
                  "text-sm font-medium transition-colors duration-300",
                  !isArabic
                    ? "text-stone-900"
                    : "text-stone-400 hover:text-stone-600"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                EN
              </Link>
              <span className="text-stone-300">|</span>
              <Link
                to={isArabic ? location.pathname : alternatePath}
                className={cn(
                  "text-sm font-medium transition-colors duration-300",
                  isArabic
                    ? "text-stone-900"
                    : "text-stone-400 hover:text-stone-600"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                عربي
              </Link>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};
